import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HelpCircle, ChevronDown, ArrowLeft, Search,
  Shirt, Camera, Wand2, CreditCard, Shield, Sparkles,
  Palette, Package, Users, Star
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import useDragScroll from '../hooks/useDragScroll';

const faqCategories = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: Sparkles,
    color: 'from-amber-500 to-orange-500',
  },
  {
    id: 'wardrobe',
    label: 'My Wardrobe',
    icon: Shirt,
    color: 'from-suede-500 to-suede-400',
  },
  {
    id: 'virtual-tryon',
    label: 'Virtual Try-On',
    icon: Camera,
    color: 'from-purple-500 to-pink-500',
  },
  {
    id: 'ai-features',
    label: 'AI Features',
    icon: Wand2,
    color: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'style-studio',
    label: 'Style Studio',
    icon: Palette,
    color: 'from-rose-500 to-pink-500',
  },
  {
    id: 'subscription',
    label: 'Subscription & Billing',
    icon: CreditCard,
    color: 'from-emerald-500 to-teal-500',
  },
  {
    id: 'privacy',
    label: 'Privacy & Security',
    icon: Shield,
    color: 'from-stone-600 to-stone-500',
  },
  {
    id: 'promoters',
    label: 'Promoters & Collabs',
    icon: Users,
    color: 'from-terracotta-500 to-terracotta-400',
  },
  {
    id: 'partner-wardrobe',
    label: 'Partner Wardrobe',
    icon: Star,
    color: 'from-pink-500 to-rose-400',
  },
  {
    id: 'friends-wardrobe',
    label: 'Friends Wardrobe',
    icon: Users,
    color: 'from-violet-500 to-purple-400',
  },
  {
    id: 'pets',
    label: 'Pets',
    icon: Package,
    color: 'from-olive-500 to-olive-400',
  },
];

const faqs = [
  // Getting Started
  {
    category: 'getting-started',
    question: 'What is fashionDisc?',
    answer: 'fashionDisc is your all-in-one fashion companion app. Upload your entire wardrobe, get AI-powered styling advice, virtually try on clothes before you buy, build outfits on a drag-and-drop canvas, discover trending products, and organise everything from capsule wardrobes to packing lists — all in one place.',
  },
  {
    category: 'getting-started',
    question: 'How do I create an account?',
    answer: 'Tap "Sign Up" on the login screen, enter your email and create a password. That\'s it — you\'re in! You can start uploading clothes and exploring features right away.',
  },
  {
    category: 'getting-started',
    question: 'Is the app free to use?',
    answer: 'Yes! The free plan gives you up to 200 wardrobe items, 10 wishlist items/month, 1 bulk upload, 1 standard virtual try-on, 1 image edit, 15 studio BG removals, and 10 background changes per month. Plus unlimited calendar outfits, canvas saves, AI Detect, and saved models. For more features like AI chats, AI Studio, and extra bulk uploads, check out our Basic (€7.99/mo) or Premium (€14.99/mo) plans. You can also buy Storage Add-Ons to increase your wardrobe capacity without upgrading.',
  },
  {
    category: 'getting-started',
    question: 'What devices is fashionDisc available on?',
    answer: 'fashionDisc is available as an iOS app on the App Store and will also be coming to Google Play for Android. You can also access it through your mobile browser.',
  },

  // Wardrobe
  {
    category: 'wardrobe',
    question: 'How do I add clothes to my wardrobe?',
    answer: 'Head to My Wardrobe and tap the "+" button. You can take a photo with your camera or upload from your photo library. Once uploaded, you can categorise the item, add details like brand, price, size, and colour, and it\'ll be saved to your digital wardrobe.',
  },
  {
    category: 'wardrobe',
    question: 'What does "Remove Background" do?',
    answer: 'Remove Background uses AI to cleanly cut out your clothing item from any background, giving you a professional, transparent look. This makes your items look great on the Style Studio canvas and in outfit combinations.',
  },
  {
    category: 'wardrobe',
    question: 'What does "AI Detect" do?',
    answer: 'AI Detect analyses your clothing photo and automatically identifies the category, colour, brand, pattern, style, and more. If you upload a screenshot from a shopping app, it can even extract the store name, price, and size from the image — saving you time on manual entry.',
  },
  {
    category: 'wardrobe',
    question: 'Can I use Remove Background and AI Detect at the same time?',
    answer: 'Yes! You can tap both buttons at the same time — they work together. Remove Background cleans up the image while AI Detect fills in all the details. Using them together is the fastest way to add items.',
  },
  {
    category: 'wardrobe',
    question: 'How do I edit or delete a clothing item?',
    answer: 'Tap on any item in your wardrobe to open its details. From there you can edit all the info — name, category, brand, price, colour, size, and more. To delete, just tap the delete button. In the Wishlist tab, you also have quick action buttons for View, Remove Background, Edit, and Delete right on each card.',
  },
  {
    category: 'wardrobe',
    question: 'What is the Capsule Wardrobe feature?',
    answer: 'Capsule Wardrobe lets you create curated mini collections from your wardrobe — like a "Work Essentials" or "Weekend Casual" capsule. Select items, give it a name, and you\'ve got a focused set of go-to pieces. You can even pack an entire capsule into a packing list with one tap.',
  },
  {
    category: 'wardrobe',
    question: 'How does the Packing feature work?',
    answer: 'The Packing feature helps you organise outfits for trips. Create a packing list, add items from your wardrobe or directly from a capsule wardrobe, and keep track of everything you need. It connects with your capsules so you can add an entire capsule to a packing list instantly.',
  },

  // Virtual Try-On
  {
    category: 'virtual-tryon',
    question: 'How does Virtual Try-On work?',
    answer: 'Upload or select a photo of yourself as your model, then choose a clothing item from your wardrobe or the product catalogue. Our AI will generate a realistic image of you wearing that item so you can see how it looks before buying or styling.',
  },
  {
    category: 'virtual-tryon',
    question: 'What kind of photos work best for try-on?',
    answer: 'Use a clear, well-lit photo where you\'re standing straight and facing the camera. Avoid heavy filters, cropped images, or photos with busy backgrounds. A simple, front-facing pose gives the best results.',
  },
  {
    category: 'virtual-tryon',
    question: 'Can I try on multiple items at once?',
    answer: 'Currently, you can try on one item at a time per generation. However, you can save your try-on results and compare different looks side by side in your try-on history.',
  },
  {
    category: 'virtual-tryon',
    question: 'How many try-ons do I get per month?',
    answer: 'Free users get 1 standard try-on per month. Basic plan includes 15 standard try-ons. Premium plan gives you 40 standard try-ons per month. Your usage resets at the start of each billing cycle.',
  },

  // AI Features
  {
    category: 'ai-features',
    question: 'What AI features are available?',
    answer: 'fashionDisc is packed with AI: Virtual Try-On, AI Detect (auto-categorises clothes), Remove Background, Style Maid / Style Guide (personalised styling agent), AI Studio (fashion videos, creative visions, 360° views, background changes), and trend analysis in the News section.',
  },
  {
    category: 'ai-features',
    question: 'How does AI styling advice work?',
    answer: 'Our AI analyses your wardrobe items, your style preferences, and current trends to suggest outfit combinations and styling tips. You can chat with the AI assistant to get personalised recommendations for any occasion.',
  },
  {
    category: 'ai-features',
    question: 'What is AI Studio?',
    answer: 'AI Studio is a creative suite for your fashion content. It includes fashion video generation, creative vision art, 360° views of your items, and background changes. It\'s perfect for content creators, promoters, or anyone who wants to see their clothes in a whole new way.',
  },
  {
    category: 'ai-features',
    question: 'How does the Style Maid / Style Guide work?',
    answer: 'The Style Maid (or Style Guide) is your personal AI fashion assistant. Tell it about your style, what occasion you\'re dressing for, or what mood you\'re going for, and it\'ll suggest outfits from your own wardrobe. You can customise its personality and the way it gives advice to match your vibe.',
  },

  // Style Studio
  {
    category: 'style-studio',
    question: 'What can I do in Style Studio?',
    answer: 'Style Studio is a visual outfit builder. Drag and drop your wardrobe items onto a canvas, resize and arrange them, change the background, and create styled flat-lay compositions. It\'s like a digital mood board for your outfits. You can see a count of items on your canvas as you build.',
  },
  {
    category: 'style-studio',
    question: 'How do I add clothes to the canvas?',
    answer: 'Open the wardrobe panel from the sidebar (or tap the "+" button on mobile) and browse or search your items. Tap any item to add it to the canvas. You\'ll see a confirmation toast and a count badge showing how many items are on your board. You can also drag items to position them exactly where you want.',
  },
  {
    category: 'style-studio',
    question: 'Can I save and share my style boards?',
    answer: 'Yes! Tap the Save button to save your canvas as an outfit. You can name it, assign a category, and it\'ll be stored in your outfit history. You can also download the canvas as an image to share on social media or with friends.',
  },

  // Subscription & Billing
  {
    category: 'subscription',
    question: 'What subscription plans are available?',
    answer: 'We offer three tiers: Free (forever, no card needed), Basic at €7.99/month, and Premium at €14.99/month. Free gives you up to 200 wardrobe items with core features. Basic gives you up to 600 wardrobe items, 30 AI chats, 15 try-ons, AI Studio access, and more. Premium is our best value with unlimited wardrobe items, unlimited AI chats & wishlist, 40 try-ons, 4 fashion videos, 2 360° views, 10 creative visions, and 6 bulk uploads per month.',
  },
  {
    category: 'subscription',
    question: 'What are Storage Add-Ons?',
    answer: 'Storage Add-Ons are available for Free plan users who want more wardrobe capacity without upgrading to a paid plan. You can purchase a Storage Tier 1 or Tier 2 add-on to increase your wardrobe item limit beyond the default 200. These are monthly auto-renewable subscriptions managed through your app store.',
  },
  {
    category: 'subscription',
    question: 'What is a Bulk Add Top-Up?',
    answer: 'A Bulk Add Top-Up is a one-time purchase that gives you extra bulk upload credits. If you\'ve used your monthly bulk uploads but need more, you can buy a top-up to get 5 additional bulk uploads. This does not auto-renew.',
  },
  {
    category: 'subscription',
    question: 'How do I upgrade or downgrade my plan?',
    answer: 'Go to your Account page and tap on your current plan or the subscription section. You\'ll see all available plans and can switch between them. Upgrades take effect immediately, and downgrades apply at the end of your current billing period.',
  },
  {
    category: 'subscription',
    question: 'How do I cancel my subscription?',
    answer: 'If you subscribed via the iOS app, go to Settings → Apple ID → Subscriptions and cancel from there. For web subscriptions, cancel through the app or contact us at fashiondisc25@gmail.com. Your subscription will remain active until the end of your current billing period.',
  },
  {
    category: 'subscription',
    question: 'Will I lose my data if I cancel?',
    answer: 'No. Your wardrobe items, saved outfits, capsules, and all your data stay in your account. You\'ll just move back to the Free plan with its usage limits. You can re-subscribe anytime to unlock premium features again.',
  },
  {
    category: 'subscription',
    question: 'What payment methods do you accept?',
    answer: 'On iOS, all payments go through Apple\'s In-App Purchase system — you pay using whatever payment method is linked to your Apple ID (credit/debit card, Apple Pay, gift card balance, etc.). On the web, we accept all major credit and debit cards through Stripe.',
  },
  {
    category: 'subscription',
    question: 'How do I restore my purchases?',
    answer: 'Go to your Account page and tap "Restore Purchases". This will check your Apple ID for any active subscriptions and re-apply them to your account. This is useful if you reinstalled the app or switched devices.',
  },

  // Privacy & Security
  {
    category: 'privacy',
    question: 'Is my data safe?',
    answer: 'Absolutely. We use industry-standard encryption for all data in transit and at rest. Your personal information is never sold to third parties. We only use your data to power the features you use within the app.',
  },
  {
    category: 'privacy',
    question: 'Are my photos stored securely?',
    answer: 'Yes. Your photos are stored securely and are only accessible to your account. They\'re used solely to power features like your wardrobe, virtual try-on, and AI detection. We do not share your photos with anyone.',
  },
  {
    category: 'privacy',
    question: 'How do I delete my account?',
    answer: 'You can request account deletion from your Account settings or by contacting us at fashiondisc25@gmail.com. Once deleted, all your data — including wardrobe items, photos, and personal information — will be permanently removed from our servers.',
  },

  // Promoters & Collabs
  {
    category: 'promoters',
    question: 'How do I become a promoter?',
    answer: 'Head to the Promoters page from your Account menu and apply.',
  },
  {
    category: 'promoters',
    question: 'Can influencers collaborate with fashionDisc?',
    answer: 'Yes! We\'re open to influencer collaborations. We offer early access to new features, exclusive tools, and community spotlight opportunities. If you\'re interested, reach out to us at fashiondisc25@gmail.com or through the Promoters page — we\'d love to work together.',
  },

  // Partner Wardrobe
  {
    category: 'partner-wardrobe',
    question: 'What is Partner Wardrobe?',
    answer: 'Partner Wardrobe lets you link your account with your boyfriend, girlfriend, or anyone you choose. Once linked, you can browse their wardrobe, pick out real clothes they own, and send them outfit suggestions — complete with item images, notes, and occasion tags. It\'s like having a personal stylist who knows exactly what\'s in your closet.',
  },
  {
    category: 'partner-wardrobe',
    question: 'How do I link with my partner?',
    answer: 'Go to My Wardrobe and tap the "Partner" button in the quick-access row (next to Wishlist, Calendar, and Packing). Tap "Link Partner", then "Generate Code" to get a 6-character invite code. Share it with your partner — they enter the code on their end, and you\'re instantly linked! Both of you can browse each other\'s wardrobes and suggest outfits.',
  },
  {
    category: 'partner-wardrobe',
    question: 'Is Partner Wardrobe available on all plans?',
    answer: 'Yes! Partner Wardrobe is available on all subscription tiers, including the Free plan. Everyone can link partners, browse their wardrobes, and send outfit suggestions at no extra cost.',
  },
  {
    category: 'partner-wardrobe',
    question: 'What can my partner see from my wardrobe?',
    answer: 'Your partner can see your wardrobe items — the photos, names, categories, and colors. They cannot edit or delete your items. They can only browse and create outfit suggestions for you using your actual clothes.',
  },
  {
    category: 'partner-wardrobe',
    question: 'How do outfit suggestions work?',
    answer: 'When browsing your partner\'s wardrobe, tap items to select them into an outfit. Add an optional note (like "Wear this for our dinner date!") and an occasion tag, then hit Send. Your partner will see the suggestion with all the actual item images and can accept or decline it.',
  },
  {
    category: 'partner-wardrobe',
    question: 'Can I unlink a partner?',
    answer: 'Yes, you can unlink a partner at any time from the Partner tab. This removes access to each other\'s wardrobes and all pending suggestions. You can always re-link with a new invite code.',
  },

  // Friends Wardrobe
  {
    category: 'friends-wardrobe',
    question: 'What is Friends Wardrobe?',
    answer: 'Friends Wardrobe works just like Partner Wardrobe but is designed for your friends! Link with as many friends as you like, browse their wardrobes, and send them outfit suggestions. It\'s perfect for helping friends pick outfits for events, sharing style inspiration, or just seeing what everyone owns.',
  },
  {
    category: 'friends-wardrobe',
    question: 'How do I add a friend?',
    answer: 'Go to My Wardrobe and tap the "Friends" button in the quick-access row. Tap "Add Friend" and generate a 6-character invite code to share, or enter a code your friend has shared with you. Once linked, you can browse each other\'s wardrobes instantly.',
  },
  {
    category: 'friends-wardrobe',
    question: 'How many friends can I link with?',
    answer: 'There\'s no limit! You can link with as many friends as you want. Each friend connection is separate, so you can browse and send suggestions to each one individually.',
  },
  {
    category: 'friends-wardrobe',
    question: 'What\'s the difference between Partner and Friends?',
    answer: 'They have the same functionality — browse wardrobes, send outfit suggestions with real item images, notes, and occasions. The difference is organisational: Partner is for your significant other (typically one person), while Friends is for your wider circle. Both are free on all plans.',
  },
  {
    category: 'friends-wardrobe',
    question: 'Can I send outfit suggestions to friends?',
    answer: 'Absolutely! Browse a friend\'s wardrobe, select items to create an outfit, add a note and occasion tag, then send it. Your friend will see the suggestion with all the actual clothing images and can accept or decline it.',
  },
  {
    category: 'friends-wardrobe',
    question: 'Can I remove a friend?',
    answer: 'Yes, you can unlink any friend at any time. This removes wardrobe access between you and clears pending suggestions. You can always re-link later with a new code.',
  },

  // Pets
  {
    category: 'pets',
    question: 'How do I add a pet?',
    answer: 'Go to My Wardrobe and toggle on the Pets mode using the paw icon. Tap "Add Pet" and fill in your pet\'s name, species, breed, size, and colour. You can also upload a photo of your pet. Hit "Add Pet" and they\'re in!',
  },
  {
    category: 'pets',
    question: 'Can I edit my pet\'s profile?',
    answer: 'Yes! Tap the pencil icon on your pet\'s card to open the edit dialog. You can update their name, photo, breed, size, colour, and species. Hit "Save Changes" when you\'re done.',
  },
  {
    category: 'pets',
    question: 'Can I add clothes for my pet?',
    answer: 'Absolutely! Once you select a pet, tap "Add Item" to add clothing and accessories to their personal wardrobe. You can upload a photo, name the item, and set its category, colour, brand, and size.',
  },
  {
    category: 'pets',
    question: 'Can I delete a pet profile?',
    answer: 'Yes, tap the trash icon on the pet\'s card to remove them. This will also remove their associated wardrobe items.',
  },

  // Wishlist
  {
    category: 'wardrobe',
    question: 'How does the Wishlist work?',
    answer: 'The Wishlist tab in My Wardrobe is for items you want to buy but don\'t own yet. Add items with a photo, name, brand, store, price, and notes. When you buy the item, tap the green shopping bag button to move it straight into your owned wardrobe.',
  },
  {
    category: 'wardrobe',
    question: 'What does the green button on wishlist items do?',
    answer: 'That\'s the "Bought" button! Tapping it moves the item from your Wishlist into your owned wardrobe automatically — it copies all the details, sets the purchase date to today, and removes it from the Wishlist. It\'s the fastest way to mark something as purchased.',
  },
  {
    category: 'wardrobe',
    question: 'What are Inspiration Boards?',
    answer: 'Inspiration Boards are Pinterest-style mood boards inside your wardrobe. Create boards with a name and description, then add images via URL or upload. Add notes to individual pins. They\'re great for collecting outfit ideas, style references, and fashion inspiration all in one place.',
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-b border-stone-100 last:border-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-4 px-1 text-left group"
    >
      <span className="text-sm font-medium text-stone-800 pr-4 group-hover:text-stone-900">
        {question}
      </span>
      <ChevronDown className={`w-4 h-4 text-stone-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          <p className="text-sm text-stone-500 pb-4 px-1 leading-relaxed">
            {answer || 'Answer coming soon.'}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);
  const chipsDrag = useDragScroll();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById('root')?.scrollTo(0, 0);
  }, []);

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = !searchQuery || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !activeCategory || faq.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedFaqs = {};
  filteredFaqs.forEach(faq => {
    if (!groupedFaqs[faq.category]) {
      groupedFaqs[faq.category] = [];
    }
    groupedFaqs[faq.category].push(faq);
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-orange-50/30 pb-12 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-stone-200/50">
        <div className="max-w-2xl mx-auto px-4 pt-[calc(env(safe-area-inset-top,0px)+12px)] pb-3 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-xl bg-stone-100 flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4 text-stone-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-stone-900">FAQ</h1>
            <p className="text-xs text-stone-500">Frequently Asked Questions</p>
          </div>
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <HelpCircle className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-stone-100/80 rounded-xl text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/30 border-0"
            />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {/* Category chips */}
        <div
          className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide"
          ref={chipsDrag.ref}
          onMouseDown={chipsDrag.onMouseDown}
          onMouseLeave={chipsDrag.onMouseLeave}
          onMouseUp={chipsDrag.onMouseUp}
          onMouseMove={chipsDrag.onMouseMove}
        >
          <button
            onClick={() => setActiveCategory(null)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              !activeCategory
                ? 'bg-stone-800 text-white'
                : 'bg-white text-stone-600 border border-stone-200'
            }`}
          >
            All
          </button>
          {faqCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                activeCategory === cat.id
                  ? 'bg-stone-800 text-white'
                  : 'bg-white text-stone-600 border border-stone-200'
              }`}
            >
              <cat.icon className="w-3 h-3" />
              {cat.label}
            </button>
          ))}
        </div>

        {/* FAQ cards by category */}
        {Object.keys(groupedFaqs).length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <HelpCircle className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 text-sm">No matching questions found</p>
            <p className="text-stone-400 text-xs mt-1">Try a different search term</p>
          </motion.div>
        ) : (
          faqCategories
            .filter(cat => groupedFaqs[cat.id])
            .map((cat, catIndex) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: catIndex * 0.05 }}
              >
                <Card className="glass-card rounded-2xl border-0 overflow-hidden">
                  {/* Category header */}
                  <div className="flex items-center gap-3 p-4 pb-2">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${cat.color} flex items-center justify-center`}>
                      <cat.icon className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-sm font-semibold text-stone-800">{cat.label}</h2>
                    <span className="text-xs text-stone-400 ml-auto">{groupedFaqs[cat.id].length}</span>
                  </div>
                  
                  {/* Questions */}
                  <div className="px-4 pb-2">
                    {groupedFaqs[cat.id].map((faq, idx) => {
                      const key = `${cat.id}-${idx}`;
                      return (
                        <FAQItem
                          key={key}
                          question={faq.question}
                          answer={faq.answer}
                          isOpen={openQuestion === key}
                          onToggle={() => setOpenQuestion(openQuestion === key ? null : key)}
                        />
                      );
                    })}
                  </div>
                </Card>
              </motion.div>
            ))
        )}

        {/* Contact section */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="glass-card rounded-2xl border-0 p-5 text-center">
            <Star className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <h3 className="text-sm font-semibold text-stone-800 mb-1">Still have questions?</h3>
            <p className="text-xs text-stone-500 mb-3">We're here to help! Reach out to us anytime.</p>
            <a
              href="mailto:fashiondisc25@gmail.com"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-suede-500 to-suede-400 text-white text-xs font-medium rounded-xl"
            >
              Contact Support
            </a>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
